import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Navbar, Offcanvas, Dropdown } from 'react-bootstrap';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useConfirmation } from '../contexts/ConfirmationContext';
import { Cart, Country, Currency } from '../types';
import CountrySelectionModal from './CountrySelectionModal';
import './Header.scss';

interface HeaderProps {
  // Props are now managed internally via API calls
}

const Header: React.FC<HeaderProps> = () => {
  const [showSidebar, setShowSidebar] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [cart, setCart] = useState<Cart | null>(null);
  const [countries, setCountries] = useState<Country[]>([]);
  const [currentCountry, setCurrentCountry] = useState<Country | null>(null);
  const [currentCurrency, setCurrentCurrency] = useState<Currency | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCartDropdown, setShowCartDropdown] = useState(false);
  const [showCountryModal, setShowCountryModal] = useState(false);
  
  const { user, logout, loading: authLoading } = useAuth();
  const { confirm } = useConfirmation();
  const location = useLocation();

  const handleCloseSidebar = () => setShowSidebar(false);
  const handleShowSidebar = () => setShowSidebar(true);
  
  // Helper function to check if a route is active
  const isActiveRoute = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.includes(path);
  };
  
  // Handle cart dropdown toggle
  const toggleCartDropdown = () => {
    setShowCartDropdown(!showCartDropdown);
  };
  
  // Get dropdown positioning based on viewport
  const getDropdownStyle = () => {
    const baseStyle = {
      display: 'block',
      right: '0',
      top: '100%',
      zIndex: 1050,
      minWidth: '320px',
      maxWidth: '400px',
      maxHeight: '80vh',
      overflowY: 'auto' as const
    };
    
    // Check if we're on mobile (viewport width < 768px)
    if (window.innerWidth < 768) {
      return {
        ...baseStyle,
        right: 'auto',
        left: '50%',
        transform: 'translateX(-50%)',
        minWidth: '280px',
        maxWidth: '90vw'
      };
    }
    
    return baseStyle;
  };
  
  // Close cart dropdown when clicking outside and handle window resize
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.cart-dropdown-container')) {
        setShowCartDropdown(false);
      }
    };
    
    const handleResize = () => {
      // Close dropdown on resize to prevent positioning issues
      if (showCartDropdown) {
        setShowCartDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', handleResize);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', handleResize);
    };
  }, [showCartDropdown]);

  // Theme toggle functionality
  const toggleTheme = async () => {
    const newTheme = !isDarkMode;
    setIsDarkMode(newTheme);
    const themeValue = newTheme ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', themeValue);
    document.documentElement.setAttribute('data-bs-theme', themeValue);
    localStorage.setItem('theme', themeValue);
    
    // Sync theme to backend
    try {
      await apiService.theme.setTheme(themeValue);
    } catch (error) {
      console.error('Error syncing theme to backend:', error);
    }
  };

  // Initialize theme on component mount - sync with Django session
  useEffect(() => {
    const initializeTheme = async () => {
      try {
        // First, try to get theme from Django's initial state
        const initialAuthState = (window as any).__INITIAL_AUTH_STATE__;
        let initialTheme = initialAuthState?.theme;
        
        // If not in initial state, try to get from backend
        if (!initialTheme) {
          try {
            const themeData = await apiService.theme.getCurrentTheme();
            initialTheme = themeData.theme;
          } catch (error) {
            console.warn('Failed to load theme from backend:', error);
          }
        }
        
        // Fallback to localStorage or system preference
        if (!initialTheme) {
          const savedTheme = localStorage.getItem('theme');
          const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
          initialTheme = savedTheme || (prefersDark ? 'dark' : 'light');
        }
        
        setIsDarkMode(initialTheme === 'dark');
        document.documentElement.setAttribute('data-theme', initialTheme);
        document.documentElement.setAttribute('data-bs-theme', initialTheme);
        localStorage.setItem('theme', initialTheme);
        
        // Sync to backend if different
        if (initialAuthState?.theme !== initialTheme) {
          try {
            await apiService.theme.setTheme(initialTheme);
          } catch (error) {
            console.error('Error syncing initial theme to backend:', error);
          }
        }
      } catch (error) {
        console.error('Error initializing theme:', error);
        // Fallback to localStorage
        const savedTheme = localStorage.getItem('theme') || 'light';
        setIsDarkMode(savedTheme === 'dark');
        document.documentElement.setAttribute('data-theme', savedTheme);
        document.documentElement.setAttribute('data-bs-theme', savedTheme);
      }
    };
    
    initializeTheme();
  }, []);

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        
        // Load data in parallel with individual error handling
        const results = await Promise.allSettled([
          apiService.cart.getCart().catch(err => {
            if (process.env.NODE_ENV === 'development') {
              console.warn('Failed to load cart:', err);
            }
            return { cart: null };
          }),
          apiService.country.getCountries().catch(err => {
            if (process.env.NODE_ENV === 'development') {
              console.warn('Failed to load countries:', err);
            }
            return { countries: [] };
          }),
          apiService.country.getCurrentCountry().catch(err => {
            if (process.env.NODE_ENV === 'development') {
              console.warn('Failed to load current country:', err);
            }
            return { country: null, currency: null, country_code: '' };
          })
        ]);

        // Handle cart data
        if (results[0].status === 'fulfilled' && results[0].value.cart) {
          setCart(results[0].value.cart);
        }

        // Handle countries data
        let loadedCountries: Country[] = [];
        if (results[1].status === 'fulfilled' && results[1].value.countries) {
          loadedCountries = results[1].value.countries;
          setCountries(loadedCountries);
        }

        // Handle current country data - also check Django's initial state
        const initialAuthState = (window as any).__INITIAL_AUTH_STATE__;
        const chooseCountry = initialAuthState?.choose_country || false;
        
        if (results[2].status === 'fulfilled') {
          const countryData = results[2].value as { country: Country | null; currency: Currency | null; country_code: string | null; requires_selection?: boolean };
          
          // Check if country requires selection (null or requires_selection flag)
          if (!countryData.country || countryData.requires_selection || chooseCountry) {
            // Show country selection modal if countries are loaded
            if (loadedCountries.length > 0) {
              setShowCountryModal(true);
            }
          } else {
            // Country is set, use it
            setCurrentCountry(countryData.country);
            setCurrentCurrency(countryData.currency);
          }
        } else if (chooseCountry && loadedCountries.length > 0) {
          // If API call failed but choose_country flag is set, show modal
          setShowCountryModal(true);
        }
        
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error loading initial data:', error);
        }
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, []);

  // Listen for cart update events
  useEffect(() => {
    const handleCartUpdate = async (event: Event) => {
      const customEvent = event as CustomEvent;
      console.log('Cart update event received:', customEvent.detail);
      try {
        // Force refresh cart data
        const cartData = await apiService.cart.getCart(true);
        setCart(cartData.cart);
        console.log('Cart data refreshed:', cartData.cart);
      } catch (error) {
        console.error('Error refreshing cart after update:', error);
      }
    };

    // Listen for custom cart update event
    window.addEventListener('cartUpdated', handleCartUpdate);
    
    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdate);
    };
  }, []);

  // Reset state when user logs out
  useEffect(() => {
    if (!user) {
      // User has logged out, reset cart and close sidebar
      setCart(null);
      setShowSidebar(false);
    }
  }, [user]);

  // Get avatar URL with fallback to UI Avatars service
  const getAvatarUrl = (userData: typeof user) => {
    if (userData?.profile?.avatar_url) {
      return userData.profile.avatar_url;
    }
    // Generate initials avatar using UI Avatars service
    const fullName = `${userData?.first_name || ''} ${userData?.last_name || ''}`.trim() || userData?.username || 'User';
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(fullName)}&background=6366f1&color=fff&size=200&bold=true`;
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
      // Reset all local state after logout
      setCart(null);
      setCountries([]);
      setCurrentCountry(null);
      setCurrentCurrency(null);
      setShowSidebar(false);
      // Refresh cart to get empty cart state
      try {
        const cartData = await apiService.cart.getCart();
        setCart(cartData.cart);
      } catch (error) {
        console.error('Error refreshing cart after logout:', error);
        setCart(null);
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Country selection
  const handleCountryChange = async (countryCode: string) => {
    try {
      console.log('Changing country to:', countryCode);
      
      // Clear all country-related cache first (including events)
      apiService.cache.clearCountryCache();
      // Also clear events cache explicitly to ensure it's cleared
      apiService.cache.clearByPattern('^events');
      
      const result = await apiService.country.setCountry(countryCode);
      setCurrentCountry(result.country);
      setCurrentCurrency(result.currency);
      
      // Close modal if open
      setShowCountryModal(false);
      
      // Refresh cart with new currency
      const cartData = await apiService.cart.getCart();
      setCart(cartData.cart);
      
      // Wait a bit to ensure session is saved on backend before dispatching event
      // This gives the backend time to save the session so API calls use the new country
      setTimeout(() => {
        // Dispatch custom event to notify other components
        window.dispatchEvent(new CustomEvent('countryChanged', {
          detail: { country: result.country, currency: result.currency }
        }));
        
        console.log('Country changed to:', result.country.name, 'Events will be filtered for this country');
      }, 200); // 200ms delay to ensure session is saved
      
    } catch (error) {
      console.error('Country change error:', error);
    }
  };

  return (
    <>
      {/* Sidebar */}
      <Offcanvas show={showSidebar} onHide={handleCloseSidebar} placement="end" className="sidebar">
        {/* User Profile Header */}
        <div className="offcanvas-header position-relative rounded-4">
          <div className="position-absolute start-0 w-100 h-100 bg-pattern opacity-10"></div>
          <div className="position-relative w-100">
            {user ? (
              <>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <Link to={`/profile/${user.username}`} className="text-decoration-none flex-grow-1" onClick={handleCloseSidebar}>
                    <div className="d-flex align-items-center">
                      <img 
                        className="img-fluid rounded-circle border border-2 border-primary me-3"
                        style={{ height: '3em', width: '3em', objectFit: 'cover' }}
                        src={getAvatarUrl(user)}
                        alt="Profile Picture"
                      />
                      <div style={{ maxWidth: '200px' }}>
                        <h5 className="mb-1">{user.first_name || user.username}</h5>
                        <p className="text-opacity-75 small mb-0">{user.email}</p>
                      </div>
                    </div>
                  </Link>
                  <div className="d-flex align-items-center gap-2">
                    {/* Theme Toggle Button for Mobile */}
                    <div className="theme-toggle" title="Toggle theme" onClick={toggleTheme}>
                      <div className="toggle-background-icons">
                        <i className="fas fa-sun toggle-icon sun"></i>
                        <i className="fas fa-moon toggle-icon moon"></i>
                      </div>
                      <div className={`toggle-slider ${isDarkMode ? 'light' : ''}`}>
                        <i className={isDarkMode ? 'fas fa-moon' : 'fas fa-sun'}></i>
                      </div>
                    </div>
                    <button 
                      type="button" 
                      className="btn-close btn-close-white" 
                      onClick={handleCloseSidebar}
                      aria-label="Close"
                    ></button>
                  </div>
                </div>
                <div className="d-flex gap-2">
                  <Link to={`/profile/${user.username}`} className="btn btn-sm btn-glass bg-opacity-25 flex-grow-1 d-flex align-items-center justify-content-center" onClick={handleCloseSidebar}>
                    <i className="fas fa-user me-2"></i>Profile
                  </Link>
                  <Link to="/chat" className="btn btn-sm btn-glass bg-opacity-25 position-relative" onClick={handleCloseSidebar}>
                    <i className="fas fa-comments"></i>
                  </Link>
                  <button 
                    className="btn btn-sm btn-glass bg-opacity-25"
                    onClick={async () => {
                      const confirmed = await confirm({
                        title: 'Logout',
                        message: 'Are you sure you want to logout?',
                        confirmText: 'Logout',
                        cancelText: 'Cancel',
                        variant: 'warning',
                        icon: 'fa-sign-out-alt'
                      });
                      if (confirmed) {
                        handleLogout();
                      }
                    }}
                  >
                    <i className="fas fa-sign-out-alt"></i>
                  </button>
                </div>
              </>
            ) : (
              <div className="d-flex justify-content-between align-items-center">
                <Link to="/accounts/login" className="btn btn-glass bg-opacity-25" onClick={handleCloseSidebar}>
                  <i className="fas fa-sign-in-alt me-2"></i>Sign In
                </Link>
                <div className="d-flex align-items-center gap-2">
                  {/* Theme Toggle Button for Mobile */}
                  <div className="theme-toggle" title="Toggle theme" onClick={toggleTheme}>
                    <div className="toggle-background-icons">
                      <i className="fas fa-sun toggle-icon sun"></i>
                      <i className="fas fa-moon toggle-icon moon"></i>
                    </div>
                    <div className={`toggle-slider ${isDarkMode ? 'light' : ''}`}>
                      <i className={isDarkMode ? 'fas fa-moon' : 'fas fa-sun'}></i>
                    </div>
                  </div>
                  <button 
                    type="button" 
                    className="btn-close btn-close-white" 
                    onClick={handleCloseSidebar}
                    aria-label="Close"
                  ></button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar Content */}
        <div className="offcanvas-body p-4">
          {authLoading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : user && (
            <>
              {/* Admin Actions - Only show if user has admin privileges */}
              {(user.is_superuser || user.profile?.has_organizations || user.profile?.has_scanner_access) && (
                <div className="mb-4">
                  <h6 className="text-primary mb-3 d-flex align-items-center">
                    <i className="fas fa-tools me-2"></i>Tools
                  </h6>
                  <div className="row g-2">
                    {user.is_superuser && (
                      <>
                        <div className="col-4">
                          <Link className="btn btn-glass bg-opacity-25 w-100 d-flex flex-column align-items-center justify-content-center py-3" to="/admin" onClick={handleCloseSidebar}>
                            <i className="fas fa-cog mb-1"></i>
                            <small className="w-100 text-center">Admin</small>
                          </Link>
                        </div>
                        <div className="col-4">
                          <Link className="btn btn-glass bg-opacity-25 w-100 d-flex flex-column align-items-center justify-content-center py-3" to="/curator" onClick={handleCloseSidebar}>
                            <i className="fas fa-share-alt mb-1"></i>
                            <small className="w-100 text-center">Curator</small>
                          </Link>
                        </div>
                        <div className="col-4">
                          <Link className="btn btn-glass bg-opacity-25 w-100 d-flex flex-column align-items-center justify-content-center py-3" to="/approval" onClick={handleCloseSidebar}>
                            <i className="fas fa-check-circle mb-1"></i>
                            <small className="w-100 text-center">Approval</small>
                          </Link>
                        </div>
                        <div className="col-4">
                          <Link className="btn btn-glass bg-opacity-25 w-100 d-flex flex-column align-items-center justify-content-center py-3" to="/reports" onClick={handleCloseSidebar}>
                            <i className="fas fa-chart-bar mb-1"></i>
                            <small className="w-100 text-center">Report</small>
                          </Link>
                        </div>
                      </>
                    )}
                    {user.profile?.has_organizations && (
                      <div className="col-4">
                        <Link className="btn btn-glass bg-opacity-25 w-100 d-flex flex-column align-items-center justify-content-center py-3" to="/orgs" onClick={handleCloseSidebar}>
                          <i className="fas fa-building mb-1"></i>
                          <small className="w-100 text-center">Orgs</small>
                        </Link>
                      </div>
                    )}
                    {user.profile?.has_scanner_access && (
                      <div className="col-4">
                        <Link className="btn btn-glass bg-opacity-25 w-100 d-flex flex-column align-items-center justify-content-center py-3" to="/scanner" onClick={handleCloseSidebar}>
                          <i className="fas fa-qrcode mb-1"></i>
                          <small className="w-100 text-center">Scanner</small>
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Quick Actions */}
              {(user.profile?.get_quick_actions && user.profile.get_quick_actions.length > 0) || user.affiliate_profile ? (
                <div className="mb-4">
                  <h6 className="text-primary mb-3 d-flex align-items-center">
                    <i className="fas fa-bolt me-2"></i>Quick Actions
                  </h6>
                  <div className="list-group list-group-flush bg-transparent">
                    {user.affiliate_profile && (
                      <Link 
                        to="/affiliate/dashboard" 
                        className={`list-group-item list-group-item-action border-0 rounded-2 mb-1 ${location.pathname === '/affiliate/dashboard/' ? 'active' : ''}`}
                        onClick={handleCloseSidebar}
                      >
                        <i className="fas fa-chart-line me-2"></i>
                        <span className="text-truncate">Affiliate Dashboard</span>
                      </Link>
                    )}
                    {user.profile?.get_quick_actions && user.profile.get_quick_actions.map((action, index) => (
                      <a
                        key={index}
                        href={action.url}
                        className={`list-group-item list-group-item-action border-0 rounded-2 mb-1 ${location.pathname === action.url ? 'active' : ''}`}
                        onClick={handleCloseSidebar}
                      >
                        <span className="text-truncate d-block">{action.name}</span>
                      </a>
                    ))}
                  </div>
                </div>
              ) : null}
            </>
          )}

          {/* Main Navigation */}
          <div className="mb-4">
            <h6 className="text-primary mb-3 d-flex align-items-center">
              <i className="fas fa-compass me-2"></i>Navigation
            </h6>
            <div className="list-group list-group-flush bg-transparent">
              <Link 
                to="/" 
                className={`list-group-item list-group-item-action border-0 rounded-2 mb-1 ${isActiveRoute('/') ? 'active' : ''}`}
                onClick={handleCloseSidebar}
              >
                <i className="fas fa-home me-2"></i>
                <span className="text-truncate">Home</span>
              </Link>
              <Link 
                to="/events" 
                className={`list-group-item list-group-item-action border-0 rounded-2 mb-1 ${isActiveRoute('/events') ? 'active' : ''}`}
                onClick={handleCloseSidebar}
              >
                <i className="fas fa-calendar-alt me-2"></i>
                <span className="text-truncate">Events</span>
              </Link>
              <Link 
                to="/activities" 
                className={`list-group-item list-group-item-action border-0 rounded-2 mb-1 ${isActiveRoute('/activities') ? 'active' : ''}`}
                onClick={handleCloseSidebar}
              >
                <i className="fas fa-map-marker-alt me-2"></i>
                <span className="text-truncate">Activities</span>
              </Link>
              <Link 
                to="/host" 
                className={`list-group-item list-group-item-action border-0 rounded-2 mb-1 ${isActiveRoute('/host') ? 'active' : ''}`}
                onClick={handleCloseSidebar}
              >
                <i className="fas fa-plus-circle me-2"></i>
                <span className="text-truncate">Host</span>
              </Link>
              <Link 
                to="/chat" 
                className={`list-group-item list-group-item-action border-0 rounded-2 mb-1 ${isActiveRoute('/chat') ? 'active' : ''}`}
                onClick={handleCloseSidebar}
              >
                <i className="fas fa-comments me-2"></i>
                <span className="text-truncate">Messages</span>
              </Link>
              <Link 
                to="/contact" 
                className={`list-group-item list-group-item-action border-0 rounded-2 mb-1 ${isActiveRoute('/contact') ? 'active' : ''}`}
                onClick={handleCloseSidebar}
              >
                <i className="fas fa-envelope me-2"></i>
                <span className="text-truncate">Contact</span>
              </Link>
            </div>
          </div>

          {/* Shopping Cart */}
          <div className="mb-4">
            <h6 className="text-primary mb-3 d-flex align-items-center">
              <i className="fas fa-shopping-cart me-2"></i>Shopping Cart
            </h6>
            {cart && cart.total_items > 0 ? (
              <>
                <div className="cart-items mb-3">
                  {cart.items.map((item, index) => (
                    <div key={index} className="card3 border-0 mb-2">
                      <div className="card-body p-3">
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="flex-grow-1 me-3" style={{ minWidth: 0 }}>
                            <p className="mb-1 small text-truncate">{item.get_item_details}</p>
                            
                            {/* Display category and cluster information */}
                            {item.event_pass_category && (
                              <div className="mb-1 d-flex flex-wrap gap-1">
                                {/* Category badge */}
                                <span className="badge bg-secondary text-white" style={{ fontSize: '0.6rem', padding: '0.2rem 0.4rem' }}>
                                  <i className="fas fa-ticket-alt me-1"></i>
                                  {item.event_pass_category.name}
                                </span>
                                
                                {/* Cluster badge if available */}
                                {item.event_pass_category.cluster && (
                                  <span 
                                    className="badge text-white"
                                    style={{ 
                                      fontSize: '0.6rem', 
                                      padding: '0.2rem 0.4rem',
                                      backgroundColor: item.event_pass_category.cluster.color || '#6c757d'
                                    }}
                                  >
                                    {item.event_pass_category.cluster.icon && (
                                      <i className={`${item.event_pass_category.cluster.icon} me-1`}></i>
                                    )}
                                    {item.event_pass_category.cluster.name}
                                  </span>
                                )}
                              </div>
                            )}
                            
                            {/* Display seat information if available */}
                            {item.get_item_details.includes('Seat') && (
                              <div className="mb-1">
                                <span className="badge bg-primary text-white" style={{ fontSize: '0.65rem', padding: '0.25rem 0.5rem' }}>
                                  <i className="fas fa-chair me-1"></i>
                                  {item.get_item_details.split(' - ').pop()}
                                </span>
                              </div>
                            )}
                            <div className="d-flex align-items-center text-muted small">
                              <span className="me-2">Qty: {item.quantity}</span>
                              <span>
                                {item.total === 0 ? 'Free' : `${cart.currency?.symbol || '$'}${item.total}`}
                              </span>
                            </div>
                          </div>
                          <button 
                            type="button" 
                            className="btn btn-sm btn-glass bg-opacity-25 flex-shrink-0"
                            onClick={async () => {
                              try {
                                await apiService.cart.removeFromCart(item.id);
                                // Refresh cart data
                                const cartData = await apiService.cart.getCart(true);
                                setCart(cartData.cart);
                              } catch (error) {
                                console.error('Error removing item from cart:', error);
                              }
                            }}
                          >
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="card3 border-0 mb-3">
                  <div className="card-body p-3">
                    <div className="d-flex justify-content-between align-items-center text-muted small mb-1">
                      <span>Subtotal:</span>
                      <span>{cart.currency?.symbol || '$'}{cart.subtotal || cart.total}</span>
                    </div>
                    {cart.vat_tax && (
                      <div className="d-flex justify-content-between align-items-center text-muted small mb-1">
                        <span>VAT:</span>
                        <span>{cart.currency?.symbol || '$'}{cart.vat_tax}</span>
                      </div>
                    )}
                    <div className="d-flex justify-content-between align-items-center text-muted small mb-2">
                      <span>Platform Fee:</span>
                      <span>{cart.currency?.symbol || '$'}{cart.platform_fee || 0}</span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <strong>Total:</strong>
                      <strong>{cart.currency?.symbol || '$'}{cart.total}</strong>
                    </div>
                  </div>
                </div>
                <Link to="/cart" className="btn btn-primary w-100 d-flex align-items-center justify-content-center gap-2" onClick={handleCloseSidebar}>
                  <i className="fas fa-shopping-cart"></i>
                  <span>Proceed to Checkout</span>
                </Link>
              </>
            ) : (
              <div className="text-center py-4">
                <i className="fas fa-shopping-cart text-muted fa-3x mb-3"></i>
                <p className="text-muted mb-3">Your cart is empty</p>
                <div className="d-grid gap-2">
                  <Link to="/events" className="btn btn-primary" onClick={handleCloseSidebar}>
                    <i className="fas fa-calendar-alt me-2"></i>Browse Events
                  </Link>
                  <Link to="/activities" className="btn btn-secondary" onClick={handleCloseSidebar}>
                    <i className="fas fa-map-marker-alt me-2"></i>Browse Activities
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </Offcanvas>

      {/* Main Navbar */}
      <Navbar expand="lg" fixed="top" className="quicket-navbar">
        <div className="nav-container">
          <div className="navbar-brand d-flex align-items-center gap-3">
            <Link to="/" className="nav-logo-link">
              <img
                src="https://static.quicket.me/webpack_bundles/logo.png"
                className="logo-light"
                style={{borderRadius: 0}}
                alt="Quicket Brand Logo."
                height="26px"
              />
              <img
                src="https://static.quicket.me/webpack_bundles/logo.png"
                style={{borderRadius: 0}}
                className="logo-dark"
                alt="Quicket Brand Logo."
                height="26px"
              />
            </Link>
            {/* Country Selector - Moved to left with logo */}
            <Dropdown>
              <Dropdown.Toggle 
                variant="outline-light" 
                size="sm" 
                className="country-selector"
                bsPrefix="country-selector-btn"
              >
                {currentCountry?.code ? (
                  <>
                    <span className={`fi fi-${(currentCountry.code || '').toLowerCase()} me-1`}></span>
                    <span className="country-code">{currentCountry.code}</span>
                  </>
                ) : (
                  <>
                    <span className="fi fi-xx me-1"></span>
                    <span className="country-code">--</span>
                  </>
                )}
              </Dropdown.Toggle>
              <Dropdown.Menu className="country-dropdown-menu">
                {countries.map((country) => (
                  <Dropdown.Item
                    key={country.id}
                    onClick={() => handleCountryChange(country.code)}
                    active={currentCountry?.code === country.code}
                  >
                    <span className={`fi fi-${(country.code || '').toLowerCase()} me-2`}></span>
                    {country.name} ({country.code})
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>

          <button
            className="btn mobile-menu-toggle d-lg-none"
            type="button"
            onClick={handleShowSidebar}
            aria-controls="sidebar"
          >
            <i className="fa-solid fa-bars-staggered"></i>
          </button>

          <div className="collapse navbar-collapse" id="navbarNav">
            {/* Left side - Logo and country selector */}
            <div className="navbar-nav me-auto">
              {/* This space is intentionally left empty to balance the centered nav */}
            </div>

            {/* Center - Main Navigation */}
            <ul className="navbar-nav mx-auto">
              <li className="nav-item">
                <Link 
                  className={`nav-link quicket-nav-link ${isActiveRoute('/') ? 'active' : ''}`} 
                  to="/"
                >
                  Home
                </Link>
              </li>
              <li className="nav-item">
                <Link 
                  className={`nav-link quicket-nav-link ${isActiveRoute('/events') ? 'active' : ''}`} 
                  to="/events"
                >
                  Events
                </Link>
              </li>
              <li className="nav-item">
                <Link 
                  className={`nav-link quicket-nav-link ${isActiveRoute('/activities') ? 'active' : ''}`} 
                  to="/activities"
                >
                  Activities
                </Link>
              </li>
              <li className="nav-item">
                <Link 
                  className={`nav-link quicket-nav-link ${isActiveRoute('/host') ? 'active' : ''}`} 
                  to="/host"
                >
                  Host
                </Link>
              </li>
              <li className="nav-item">
                <Link 
                  className={`nav-link quicket-nav-link ${isActiveRoute('/contact') ? 'active' : ''}`} 
                  to="/contact"
                >
                  Contact
                </Link>
              </li>
            </ul>

            {/* Right side - User actions and cart */}
            <div className="navbar-nav ms-auto d-flex align-items-center">
              {/* Theme Toggle */}
              <div className="theme-toggle me-3" title="Toggle theme" onClick={toggleTheme}>
                <div className="toggle-background-icons">
                <i className="fas fa-sun toggle-icon sun"></i>
                <i className="fas fa-moon toggle-icon moon"></i>
                </div>
                <div className={`toggle-slider ${isDarkMode ? 'light' : ''}`}>
                  <i className={isDarkMode ? 'fas fa-moon' : 'fas fa-sun'}></i>
                </div>
              </div>

              {/* Cart Dropdown */}
              <div className="dropdown me-3 cart-dropdown-container position-relative">
                <button 
                  type="button" 
                  className="btn cart-toggle-btn position-relative" 
                  onClick={toggleCartDropdown}
                  aria-expanded={showCartDropdown}
                >
                  <i className="fas fa-shopping-bag"></i>
                  {cart && cart.total_items > 0 && (
                    <span className="cart-badge">{cart.total_items}</span>
                  )}
                </button>
                {showCartDropdown && (
                  <div 
                    className="dropdown-menu facebook-cart-dropdown show position-absolute" 
                    style={getDropdownStyle()}
                  >
                  <div className="dropdown-header d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Cart</h6>
                  </div>
                  {cart && cart.total_items > 0 ? (
                    <>
                      <div className="cart-items mb-3">
                        {cart.items.map((item, index) => (
                          <div key={index} className="d-flex justify-content-between align-items-center mb-2">
                            <div className="flex-grow-1 me-3">
                              <p className="mb-1 small">{item.get_item_details}</p>
                              
                              {/* Display category and cluster information */}
                              {item.event_pass_category && (
                                <div className="mb-1 d-flex flex-wrap gap-1">
                                  {/* Category badge */}
                                  <span className="badge bg-secondary text-white" style={{ fontSize: '0.6rem', padding: '0.2rem 0.4rem' }}>
                                    <i className="fas fa-ticket-alt me-1"></i>
                                    {item.event_pass_category.name}
                                  </span>
                                  
                                  {/* Cluster badge if available */}
                                  {item.event_pass_category.cluster && (
                                    <span 
                                      className="badge text-white"
                                      style={{ 
                                        fontSize: '0.6rem', 
                                        padding: '0.2rem 0.4rem',
                                        backgroundColor: item.event_pass_category.cluster.color || '#6c757d'
                                      }}
                                    >
                                      {item.event_pass_category.cluster.icon && (
                                        <i className={`${item.event_pass_category.cluster.icon} me-1`}></i>
                                      )}
                                      {item.event_pass_category.cluster.name}
                                    </span>
                                  )}
                                </div>
                              )}
                              
                              {/* Display seat information if available */}
                              {item.get_item_details.includes('Seat') && (
                                <div className="mb-1">
                                  <span className="badge bg-primary text-white" style={{ fontSize: '0.65rem', padding: '0.25rem 0.5rem' }}>
                                    <i className="fas fa-chair me-1"></i>
                                    {item.get_item_details.split(' - ').pop()}
                                  </span>
                                </div>
                              )}
                              <div className="d-flex align-items-center text-muted small">
                                <span className="me-2">Qty: {item.quantity}</span>
                                <span>
                                  {item.total === 0 ? 'Free' : `${cart.currency?.symbol || '$'}${item.total}`}
                                </span>
                              </div>
                            </div>
                            <button 
                              type="button" 
                              className="btn btn-sm btn-outline-danger"
                              onClick={async () => {
                                try {
                                  await apiService.cart.removeFromCart(item.id);
                                  // Refresh cart data
                                  const cartData = await apiService.cart.getCart(true);
                                  setCart(cartData.cart);
                                } catch (error) {
                                  console.error('Error removing item from cart:', error);
                                }
                              }}
                            >
                              <i className="fas fa-trash-alt"></i>
                            </button>
                          </div>
                        ))}
                      </div>
                      <div className="dropdown-divider"></div>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <strong>Total:</strong>
                        <strong>{cart.currency?.symbol || '$'}{cart.total}</strong>
                      </div>
                      <Link 
                        to="/cart" 
                        className="btn btn-primary w-100 d-flex align-items-center justify-content-center gap-2"
                        onClick={() => setShowCartDropdown(false)}
                      >
                        <i className="fas fa-shopping-cart"></i>
                        <span>Proceed to Checkout</span>
                      </Link>
                    </>
                  ) : (
                    <div className="text-center py-3">
                      <i className="fas fa-shopping-cart text-muted fa-2x mb-2"></i>
                      <p className="text-muted mb-3">Your cart is empty</p>
                      <div className="d-grid gap-2">
                        <Link 
                          to="/events" 
                          className="btn btn-primary btn-sm"
                          onClick={() => setShowCartDropdown(false)}
                        >
                          <i className="fas fa-calendar-alt me-2"></i>Browse Events
                        </Link>
                        <Link 
                          to="/activities" 
                          className="btn btn-secondary btn-sm"
                          onClick={() => setShowCartDropdown(false)}
                        >
                          <i className="fas fa-map-marker-alt me-2"></i>Browse Activities
                        </Link>
                      </div>
                    </div>
                  )}
                  </div>
                )}
              </div>

              {/* User Profile */}
              {user ? (
                <button 
                  className="btn user-profile-btn"
                  type="button"
                  onClick={handleShowSidebar}
                >
                  <span className="d-flex align-items-center">
                    <img 
                      src={getAvatarUrl(user)} 
                      alt="" 
                      className="rounded-circle me-2"
                      style={{ height: '1.8em', width: '1.8em', objectFit: 'cover' }}
                    />
                    {user.first_name || user.username}
                  </span>
                </button>
              ) : (
                <Link to="/accounts/login" className="btn sign-in-btn">
                  Sign In
                </Link>
              )}
            </div>
          </div>
        </div>
      </Navbar>

      {/* Country Selection Modal */}
      <CountrySelectionModal
        show={showCountryModal}
        countries={countries}
        onSelect={handleCountryChange}
      />
    </>
  );
};

export default Header;
